import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Eye, EyeOff, UserPlus } from 'lucide-react';
import axios from 'axios';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_link-builder-22/artifacts/o4eavll4_a942b2bb-6513-4b93-b220-bd15a0be07b2.jpg';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return; }
    setLoading(true);
    try {
      await register(name, email, password);
      // Apply referral code if provided
      if (referralCode.trim()) {
        try {
          await axios.post(`${API}/referral/apply`, { code: referralCode.trim() }, { withCredentials: true });
        } catch {}
      }
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative" data-testid="register-page">
      <div className="absolute inset-0 bg-gradient-to-t from-red-900/10 via-transparent to-transparent" />
      <div className="w-full max-w-md relative z-10 animate-fade-in-up">
        <div className="flex flex-col items-center mb-12">
          <img src={LOGO_URL} alt="Diavel Tattoo" className="w-20 h-20 object-contain mb-6" />
          <h1 className="font-heading font-extrabold text-3xl tracking-tight uppercase text-white">INFERNO</h1>
          <p className="font-mono text-xs tracking-wider uppercase text-zinc-500 mt-2">Únete al club</p>
        </div>

        <div className="bg-diavel-card border border-white/5 p-8">
          <h2 className="font-heading font-bold text-xl uppercase tracking-wide text-white mb-8">Crear Cuenta</h2>
          
          <button
            onClick={handleGoogleRegister}
            data-testid="google-register-btn"
            className="w-full flex items-center justify-center gap-3 bg-white text-black font-bold py-3 px-4 hover:bg-zinc-200 transition-colors duration-300 mb-6"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Registrarse con Google
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-white/10" />
            <span className="font-mono text-xs tracking-wider uppercase text-zinc-600">o</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div data-testid="register-error" className="bg-red-900/20 border border-red-600/30 text-red-400 text-sm p-3">{error}</div>
            )}
            <div>
              <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Nombre</label>
              <input
                data-testid="register-name-input"
                type="text" value={name} onChange={e => setName(e.target.value)}
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 focus:ring-1 focus:ring-red-600/50 h-12 px-4 text-white placeholder:text-zinc-600 outline-none transition-all duration-300"
                placeholder="Tu nombre" required
              />
            </div>
            <div>
              <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Email</label>
              <input
                data-testid="register-email-input"
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 focus:ring-1 focus:ring-red-600/50 h-12 px-4 text-white placeholder:text-zinc-600 outline-none transition-all duration-300"
                placeholder="tu@email.com" required
              />
            </div>
            <div>
              <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Contraseña</label>
              <div className="relative">
                <input
                  data-testid="register-password-input"
                  type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 focus:ring-1 focus:ring-red-600/50 h-12 px-4 pr-12 text-white placeholder:text-zinc-600 outline-none transition-all duration-300"
                  placeholder="Mínimo 6 caracteres" required
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
                  {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="font-mono text-xs tracking-wider uppercase text-zinc-500 block mb-2">Codigo de referido <span className="text-zinc-700">(opcional)</span></label>
              <input
                data-testid="register-referral-input"
                type="text" value={referralCode} onChange={e => setReferralCode(e.target.value.toUpperCase())}
                className="w-full bg-zinc-900/50 border border-white/10 focus:border-red-600 focus:ring-1 focus:ring-red-600/50 h-12 px-4 text-white placeholder:text-zinc-600 outline-none transition-all duration-300 font-mono tracking-wider"
                placeholder="DV3A8F2B"
              />
            </div>
            <button
              data-testid="register-submit-btn"
              type="submit" disabled={loading}
              className="w-full bg-diavel-red text-white font-bold uppercase tracking-wider py-3 px-8 hover:bg-red-700 transition-all duration-300 hover:shadow-[0_0_20px_rgba(196,30,30,0.4)] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <span className="animate-pulse">Creando cuenta...</span> : <><UserPlus className="w-4 h-4" /> Crear Cuenta</>}
            </button>
          </form>

          <p className="text-center text-zinc-500 text-sm mt-6">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" data-testid="login-link" className="text-diavel-red hover:text-red-400 transition-colors font-semibold">Iniciar sesión</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
